import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService';
import styles from './AdminOrdersPage.module.scss';
import commonStyles from '../AdminOrdersPage/AdminOrdersPage.module.scss';
import { toast } from 'react-toastify';
import { IoFilter, IoRefresh, IoTrash } from 'react-icons/io5';

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all'); 
  const [filterValue, setFilterValue] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = (type = 'all', value = '') => {
    setLoading(true);
    adminService.getOrders(type, value)
      .then(response => {
        setOrders(response.data);
        setLoading(false);
      })
      .catch(err => {
        toast.error("Lỗi tải danh sách đơn hàng");
        setLoading(false);
      });
  };

  const handleFilter = (e) => {
    e.preventDefault();
    loadOrders(filterType, filterValue);
  };

  const handleReset = () => {
    setFilterType('all');
    setFilterValue('');
    loadOrders('all', '');
  };

  const formatDate = (dateString) => {
    const options = { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleString('vi-VN', options);
  };
  
  const formatPrice = (price) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);

  // --- CẤU HÌNH TRẠNG THÁI (TỪ ĐIỂN) ---
  const STATUS_MAP = {
    'Pending':   { label: 'Chờ xác nhận', class: 'pending' },
    'Confirmed': { label: 'Đã xác nhận',  class: 'confirmed' },
    'Shipped':   { label: 'Đang giao',    class: 'shipped' },
    'Completed': { label: 'Hoàn thành',   class: 'completed' },
    'Cancelled': { label: 'Đã hủy',       class: 'cancelled' },
  };
  // Mảng các lựa chọn cho Dropdown (Tiếng Việt)
  const statusOptions = Object.values(STATUS_MAP).map(s => s.label);

  // Hàm cập nhật trạng thái
  const handleStatusChange = (orderId, newStatusLabel) => {
    // API của bạn đã được sửa để tự nhận diện Tiếng Việt và chuyển sang Anh
    // Nên chúng ta gửi trực tiếp label Tiếng Việt (newStatusLabel)
    adminService.updateOrderStatus(orderId, newStatusLabel)
      .then(() => {
        toast.success(`Đã cập nhật #${orderId} thành "${newStatusLabel}"`);
        // Cập nhật lại UI: Chúng ta cần tìm key Tiếng Anh tương ứng để update state local
        const newStatusKey = Object.keys(STATUS_MAP).find(key => STATUS_MAP[key].label === newStatusLabel);
        
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId ? { ...order, status: newStatusKey } : order
          )
        );
      })
      .catch(err => toast.error("Lỗi: " + err.response.data.message));
  };

  // Hàm xóa đơn hàng
  const handleDeleteOrder = (orderId) => {
    if (window.confirm(`Bạn có chắc muốn xóa vĩnh viễn đơn hàng #${orderId}?`)) {
      adminService.deleteOrder(orderId)
        .then(() => {
          toast.success("Đã xóa đơn hàng.");
          loadOrders(filterType, filterValue);
        })
        .catch(err => toast.error("Lỗi xóa: " + (err.response?.data?.message || err.message)));
    }
  };
  
  return (
    <div className={commonStyles.adminPage}>
      <h1>Quản Lý Đơn Hàng</h1>
      
      <div className={styles.filterBar}>
        <form onSubmit={handleFilter} className={styles.filterForm}>
          <select 
            value={filterType} 
            onChange={(e) => { setFilterType(e.target.value); setFilterValue(''); }}
            className={styles.filterSelect}
          >
            <option value="all">Tất cả</option>
            <option value="date">Theo Ngày</option>
            <option value="month">Theo Tháng</option>
            <option value="year">Theo Năm</option>
          </select>

          {filterType === 'date' && <input type="date" required value={filterValue} onChange={e => setFilterValue(e.target.value)} className={styles.filterInput} />}
          {filterType === 'month' && <input type="month" required value={filterValue} onChange={e => setFilterValue(e.target.value)} className={styles.filterInput} />}
          {filterType === 'year' && <input type="number" placeholder="Năm (VD: 2025)" required min="2020" max="2099" value={filterValue} onChange={e => setFilterValue(e.target.value)} className={styles.filterInput} />}

          <button type="submit" className={styles.filterButton} disabled={filterType === 'all'}>
            <IoFilter /> Lọc
          </button>
          <button type="button" onClick={handleReset} className={styles.resetButton}>
            <IoRefresh /> Làm mới
          </button>
        </form>
      </div>

      {loading ? <p>Đang tải...</p> : (
        <table className={commonStyles.adminTable}>
          <thead>
            <tr>
              <th>Mã ĐH</th>
              <th>Khách Hàng</th>
              <th>Tổng Tiền</th>
              <th>Trạng Thái</th>
              <th>Ngày Đặt</th>
              <th>Hành Động</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan="6" style={{textAlign: 'center', padding: '20px'}}>Chưa có đơn hàng nào.</td></tr>
            ) : (
              orders.map(order => {
                // Lấy thông tin hiển thị từ Map
                // Nếu order.status (Tiếng Anh) có trong Map, dùng label Tiếng Việt
                // Nếu không (ví dụ data cũ lỗi), dùng chính nó
                const displayStatus = STATUS_MAP[order.status]?.label || order.status;
                const statusClass = STATUS_MAP[order.status]?.class || 'pending';

                return (
                  <tr key={order.id}>
                    <td data-label="Mã ĐH">#{order.id}</td>
                    <td data-label="Khách Hàng">{order.customer_name}</td>
                    <td data-label="Tổng Tiền">{formatPrice(order.total_amount)}</td>
                    <td data-label="Trạng Thái">
                      <select 
                        value={displayStatus} 
                        className={`${styles.statusSelect} ${styles[statusClass]}`}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      >
                        {statusOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td data-label="Ngày Đặt">{formatDate(order.created_at)}</td>
                    <td data-label="Hành Động">
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <Link to={`/admin/orders/${order.id}`} className={commonStyles.actionButton}>
                          Xem
                        </Link>
                        {/* Chỉ hiện nút xóa khi Đã hủy */}
                        {(order.status === 'Cancelled') && (
                          <button 
                            onClick={() => handleDeleteOrder(order.id)}
                            className={`${commonStyles.actionButton} ${styles.deleteButton}`}
                            title="Xóa vĩnh viễn"
                          >
                            <IoTrash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminOrdersPage;